import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyDto, BusinessSectorDto } from '@jaam/commons/dtos';
import { Search24Regular } from '@fluentui/react-icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ListLoader } from '../ui';
import EmptyPlaceholder from '../emptyPlaceholder/emptyPlaceholder';

export interface CompanyWithStats extends CompanyDto {
  businessSector?: BusinessSectorDto;
  sectorName?: string;
  employeesCount?: number;
  independentWorkersCount?: number;
}

interface CompanySelectionPageProps {
  companies: CompanyWithStats[];
  isLoading?: boolean;
  onCompanyClick: (company: CompanyDto) => void;
  onSearch?: (searchTerm: string) => void;
}

const getCompanyInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return `${words[0].charAt(0).toUpperCase()}${words[1].charAt(0).toUpperCase()}`;
  }
  return name.substring(0, 2).toUpperCase();
};

const getCompanyColor = (name: string): string => {
  const colors = [
    'bg-teal-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-pink-500',
    'bg-indigo-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const CompanySelectionPage: React.FC<CompanySelectionPageProps> = ({
  companies,
  isLoading = false,
  onCompanyClick,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return companies;
    const lowerSearch = searchTerm.toLowerCase();
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(lowerSearch) ||
        company.businessSector?.name?.toLowerCase().includes(lowerSearch) ||
        company.sectorName?.toLowerCase().includes(lowerSearch),
    );
  }, [companies, searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  return (
    <div className="h-screen px-4 py-8 overflow-y-auto bg-neutral-98">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold ">
            {t('companySelection.welcomeBackDefault')}
          </h1>

        </div>

        {/* Search Bar */}
        <div className="grid justify-between grid-cols-1 gap-2 mb-8 md:grid-cols-2 md:h-10">
          <h2 className="text-lg ">
            {t('companySelection.yourCompanies')}
          </h2>
          <Input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t('companySelection.searchPlaceholder')}
            icon={<Search24Regular className="w-5 h-5" />}
            iconPosition="start"
            disabled={!navigator.onLine}
            disableBg
            inputSize="sm"
            className="bg-white border rounded-lg border-neutral-90"
          />
        </div>

        {/* Companies Grid */}
        {isLoading ? (
          <ListLoader />
        ) : filteredCompanies.length === 0 ? (
          <EmptyPlaceholder customMessage={t('companySelection.noCompaniesFound')} />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCompanies.map((company) => {
              const initials = getCompanyInitials(company.name);
              const colorClass = getCompanyColor(company.name);

              return (
                <div
                  key={company.id}
                  className="p-6 transition-shadow bg-white rounded-2xl hover:shadow-lg hover:bg-blue-95"
                  style={{ boxShadow: '0px 2px 8px 0px #00000014' }}
                >
                  {/* Company Logo/Icon and Name Section */}

                  <div className="flex items-start gap-4 mb-4">
                    {/* Company Logo/Icon */}
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="flex-shrink-0 object-cover w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-full ${colorClass} flex items-center justify-center text-white text-lg font-semibold flex-shrink-0`}
                      >
                        {initials}
                      </div>
                    )}

                    {/* Company Name and Sector */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-left text-neutral-20">
                        {company.name}
                      </h3>
                      {(company.businessSector?.name || company.sectorName) && (
                        <p className="text-sm text-left text-neutral-40 line-clamp-1">
                          {company.businessSector?.name || company.sectorName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="mb-6 space-y-2">
                    {company.employeesCount !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-40">
                          {t('companySelection.employees')}:
                        </span>
                        <span className="font-semibold text-neutral-20">
                          {company.employeesCount.toString().padStart(2, '0')}
                        </span>
                      </div>
                    )}
                    {company.independentWorkersCount !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-40">
                          {t('companySelection.independentWorkers')}:
                        </span>
                        <span className="font-semibold text-neutral-20">
                          {company.independentWorkersCount.toString().padStart(2, '0')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Access Button */}
                  <Button
                    onClick={() => onCompanyClick(company)}
                    className="w-full text-white bg-primary hover:bg-primary/90"
                    disabled={!navigator.onLine}
                  >
                    {t('companySelection.accessCompany')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySelectionPage;
